import { useEffect } from "react";
import { SITE } from "../data/site";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLink(rel, href, extra = {}) {
  if (!href) return;
  const selectorParts = [`link[rel="${rel}"]`];
  if (extra.hreflang) selectorParts.push(`[hreflang="${extra.hreflang}"]`);
  let tag = document.head.querySelector(selectorParts.join(""));
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => tag.setAttribute(k, v));
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

const JSONLD_ID = "fsg-jsonld-page";

/**
 * SEO por página sin dependencias externas: título, descripción,
 * canónica, Open Graph, Twitter Card, hreflang y JSON-LD.
 */
export default function useSeo({
  title,
  description,
  path = "/",
  image,
  lang = "es",
  type = "website",
  jsonLd,
  noindex = false,
} = {}) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} | ${SITE.shortName}`
      : `${SITE.name} — ${SITE.tagline.es}`;
    const url = `${SITE.url}${path}`;
    const img = image ? `${SITE.url}${image}` : `${SITE.url}${SITE.ogImage}`;

    document.title = fullTitle;
    document.documentElement.setAttribute("lang", lang);

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:locale", lang === "en" ? "en_US" : "es_PR");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    upsertLink("canonical", url);
    upsertLink("alternate", url, { hreflang: "es-pr" });
    upsertLink("alternate", url, { hreflang: "en-us" });
    upsertLink("alternate", url, { hreflang: "x-default" });

    const previous = document.getElementById(JSONLD_ID);
    if (previous) previous.remove();

    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = JSONLD_ID;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const node = document.getElementById(JSONLD_ID);
      if (node) node.remove();
    };
  }, [title, description, path, image, lang, type, jsonLd, noindex]);
}

/* ---------- Constructores de datos estructurados ---------- */

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    name: SITE.name,
    alternateName: SITE.shortName,
    url: SITE.url,
    logo: `${SITE.url}${SITE.logo}`,
    image: `${SITE.url}${SITE.ogImage}`,
    telephone: SITE.phone,
    email: SITE.email,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: `${SITE.address.street}, ${SITE.address.district}`,
      addressLocality: SITE.address.city,
      addressRegion: "PR",
      postalCode: SITE.address.zip,
      addressCountry: "US",
    },
    areaServed: { "@type": "AdministrativeArea", name: "Puerto Rico" },
    sameAs: [SITE.social.facebook, SITE.social.instagram],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
        ],
        opens: "08:30",
        closes: "17:30",
      },
    ],
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE.url}${item.path}`,
    })),
  };
}

export function faqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    })),
  };
}

export function articleJsonLd({ title, description, path, date, category }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    articleSection: category,
    datePublished: date,
    dateModified: date,
    mainEntityOfPage: `${SITE.url}${path}`,
    author: { "@type": "Organization", name: SITE.name },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: `${SITE.url}${SITE.logo}` },
    },
  };
}

export function productJsonLd({ name, description, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    name,
    description,
    url: `${SITE.url}${path}`,
    areaServed: { "@type": "AdministrativeArea", name: "Puerto Rico" },
    provider: { "@type": "InsuranceAgency", name: SITE.name, url: SITE.url },
  };
}
